"use strict";
Object.defineProperties(exports, { __esModule: { value: true }, [Symbol.toStringTag]: { value: "Module" } });
const moment = require("moment");
const momentTimezone = require("moment-timezone");
const numeral = require("numeral");
const _interopDefaultCompat = (e) => e && typeof e === "object" && "default" in e ? e : { default: e };
const moment__default = /* @__PURE__ */ _interopDefaultCompat(moment);
const numeral__default = /* @__PURE__ */ _interopDefaultCompat(numeral);
const processRow = (row) => {
  let finalVal = "";
  for (let i = 0; i < row.length; i += 1) {
    const innerValue = row[i] === null ? "" : row[i].toString();
    let result = innerValue.replace(/"/g, '""');
    if (result.search(/("|,|\n)/g) >= 0)
      result = `"${result}"`;
    if (i > 0)
      finalVal += ",";
    finalVal += result;
  }
  return `${finalVal}
`;
};
const buildFile = (header, rows) => {
  const csv = [];
  csv.push(processRow(header));
  rows.forEach((row) => {
    csv.push(processRow(row));
  });
  return csv;
};
const download = (csv, filename) => {
  const csvFile = new Blob(csv, { type: "text/csv" });
  const downloadLink = document.createElement("a");
  downloadLink.download = filename;
  downloadLink.href = window.URL.createObjectURL(csvFile);
  downloadLink.style.display = "none";
  document.body.appendChild(downloadLink);
  downloadLink.click();
};
const dateFormats = {
  short: "MM/DD/YYYY",
  iso: "Y-MM-DD",
  condensed: "MMM D, YYYY",
  month: "MM/YYYY",
  monthCondensed: "MMM YYYY",
  longWithAt: "MM/DD/YYYY [at] hh:mma z",
  long: "MM/DD/YYYY hh:mma z",
  timeOnly: "hh:mma z"
};
const guessTimezone = () => momentTimezone.tz.guess();
const dateFormat = (value, config = {}) => {
  if (!value) {
    return null;
  }
  const { format = "short", timezone } = config;
  const userTimezone = timezone || guessTimezone();
  const formatWithTimezone = () => momentTimezone.tz(value, "UTC").tz(userTimezone).format(dateFormats[format]);
  const formattedWithoutTimezone = () => moment__default.default(value).format(dateFormats[format]);
  const formatDate = timezone ? formatWithTimezone : formattedWithoutTimezone;
  switch (format) {
    case "short":
    case "condensed":
    case "iso":
    case "month":
    case "monthCondensed":
      return formatDate();
    case "long":
    case "longWithAt":
    case "timeOnly": {
      return momentTimezone.tz(value, "YYYY-MM-DDThh:mm:ss", "UTC").tz(userTimezone).format(dateFormats[format]);
    }
    default:
      return null;
  }
};
const condensedDateFormat = (value) => {
  return dateFormat(value, { format: "condensed" });
};
const greaterThanOrEqualDate = (firstDate, secondDate) => {
  return moment__default.default(firstDate).isSameOrAfter(moment__default.default(secondDate));
};
const greaterThanOrEqualToday = (value) => {
  const yesterday = moment__default.default().subtract(1, "day");
  return moment__default.default(value).isAfter(yesterday);
};
const lessThanOrEqualDate = (firstDate, secondDate) => {
  return moment__default.default(firstDate).isSameOrBefore(moment__default.default(secondDate));
};
const lessThanToday = (value) => {
  return moment__default.default(value).isBefore(moment__default.default());
};
const getRoundingFunction = (round = "round") => round === "none" ? Math.trunc : Math[round];
const formatNumber = (number, format, config = {}) => {
  let formatted = numeral__default.default(number).format(format, getRoundingFunction(config.rounding));
  if (config.minPrecision) {
    const mantissa = formatted.split(".")[1];
    if (mantissa.length > config.minPrecision) {
      formatted = formatted.replace(/\.?0+$/, "");
    }
    if (formatted.split(".")[1].length < config.minPrecision) {
      formatted += `${"0".repeat(config.minPrecision - formatted.split(".")[1].length)}`;
    }
  }
  return formatted;
};
const formatPercent = (value, config = {}) => {
  const { alwaysShow = false, precision = 2 } = config;
  if (!value && !alwaysShow) {
    return "";
  }
  return formatNumber(value, `0.${"0".repeat(precision)}%`);
};
const formatHumanizedNumber = (value, config = {}) => {
  const { alwaysShow = false } = config;
  if (!value && !alwaysShow) {
    return "";
  } else if (value % 1 === 0) {
    return formatNumber(value, "0,0");
  }
  return formatNumber(value, "0,0.00");
};
const getFractionalFormat = (decimalsAllowed) => {
  return decimalsAllowed ? `0,0.[${"0".repeat(decimalsAllowed)}]` : "0,0";
};
const intcomma = (value, maxDecimalPlaces = 20) => {
  if (value === 0) {
    return "0";
  }
  const val = Number(value);
  if (!Number.isFinite(val)) {
    return "0";
  }
  const parts = val.toString().split(".");
  const firstPart = parts[0];
  let returnValue = firstPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  if (parts.length === 2) {
    const decimals = parts[1].slice(0, maxDecimalPlaces);
    returnValue += `.${decimals}`;
  }
  return returnValue;
};
const capitalizeWord = (word = "") => `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
const capitalizeAllWords = (value = "") => value.split(" ").map(capitalizeWord).join(" ");
const capitalize = (value = "", config = {}) => {
  const { allWords = false } = config;
  if (typeof value !== "string") {
    throw new Error(`capitalize expects a string, received: ${value}`);
  }
  return allWords ? capitalizeAllWords(value) : capitalizeWord(value);
};
exports.buildFile = buildFile;
exports.capitalize = capitalize;
exports.condensedDateFormat = condensedDateFormat;
exports.dateFormat = dateFormat;
exports.download = download;
exports.formatHumanizedNumber = formatHumanizedNumber;
exports.formatNumber = formatNumber;
exports.formatPercent = formatPercent;
exports.getFractionalFormat = getFractionalFormat;
exports.getRoundingFunction = getRoundingFunction;
exports.greaterThanOrEqualDate = greaterThanOrEqualDate;
exports.greaterThanOrEqualToday = greaterThanOrEqualToday;
exports.intcomma = intcomma;
exports.lessThanOrEqualDate = lessThanOrEqualDate;
exports.lessThanToday = lessThanToday;
//# sourceMappingURL=index.js.map
